import { useState } from "preact/hooks";
import { errorMessage, joinRoom } from "../../../api/client";
import type { RoomInfo } from "../../../api/types";
import { startGame } from "../../../state/game";
import { LockIcon } from "../../components/Badge";
import { Button } from "../../components/Button";
import { Field, TextInput } from "../../components/Field";
import { Modal } from "../../components/Modal";
import { PASSWORD_MAX } from "./lobbySettings";
import { useAlive } from "./useAlive";

/** Modal focuses `[data-autofocus]` first and TextInput has no passthrough for it, so tag the input on mount. */
function markAutofocus(el: HTMLInputElement | null): void {
  el?.setAttribute("data-autofocus", "");
}

export interface RoomPasswordModalProps {
  room: RoomInfo;
  onClose: () => void;
}

/**
 * Asks for the password of a protected room and joins it. Failures (wrong password, full room,
 * network) stay in the dialog under the field; success starts the game and the screen unmounts.
 */
export function RoomPasswordModal({ room, onClose }: RoomPasswordModalProps) {
  const alive = useAlive();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (busy) return;
    if (password === "") {
      setError("Enter the room password");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const join = await joinRoom(room.id, password);
      // The busy state stays until the game screen replaces this one.
      if (alive.current) startGame(join, password);
    } catch (e) {
      if (!alive.current) return;
      setBusy(false);
      setError(errorMessage(e));
    }
  };

  return (
    <Modal
      title="Password required"
      size="sm"
      onClose={onClose}
      dismissible={!busy}
      footer={
        <>
          <Button variant="ghost" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={busy} onClick={() => void submit()}>
            Join room
          </Button>
        </>
      }
    >
      <div class="rooms-pw__intro">
        <span class="rooms-pw__lock" aria-hidden="true">
          <LockIcon size={18} />
        </span>
        <p class="rooms-pw__text">
          <strong class="rooms-pw__room">{room.name}</strong> is protected. Enter its password to join.
        </p>
      </div>
      <Field label="Password" error={error}>
        {(id) => (
          <TextInput
            id={id}
            type="password"
            value={password}
            onValue={(value) => {
              setPassword(value);
              setError(null);
            }}
            maxLength={PASSWORD_MAX}
            autoComplete="off"
            invalid={error !== null}
            inputRef={markAutofocus}
            onEnter={() => void submit()}
          />
        )}
      </Field>
    </Modal>
  );
}
